import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/base/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";
import type { Movie } from "@/types";

const shortcuts = [
  {
    icon: "ri-film-line",
    title: "全部系列电影",
    desc: "八部片目完整收录",
    to: "/category",
  },
  {
    icon: "ri-time-line",
    title: "最新上映",
    desc: "近年新作与纪念篇",
    to: "/category?tag=最新上映",
  },
  {
    icon: "ri-heart-3-line",
    title: "经典系列",
    desc: "九十年代原初记忆",
    to: "/category?tag=经典系列",
  },
  {
    icon: "ri-star-line",
    title: "高分推荐",
    desc: "按评分重新排序",
    to: "/category?sort=rating",
  },
];

const featured = ["peach-2024", "peach-2025", "peach-1993", "peach-2023"]
  .map((id) => movies.find((movie) => movie.id === id))
  .filter((movie): movie is Movie => Boolean(movie));

const topics = newsList.filter((item) => item.category === "专题").slice(0, 3);
const latestNews = newsList.slice(0, 4);
const timeline = [...movies].sort((a, b) => a.year - b.year);

export default function Home() {
  useSeo({
    title: "蜜桃成熟时电影大全 - 高清剧情介绍与影视资讯",
    description:
      "蜜桃成熟时电影为您提供全面的电影剧情介绍、演员信息及相关影视资讯，涵盖经典与新作，让影迷轻松掌握最新动态。",
    keywords: "蜜桃成熟时电影,蜜桃成熟时,电影剧情,影视资讯,电影介绍,电影演员,电影大全",
  });

  return (
    <SiteLayout overlayHeader>
      <HeroCarousel />

      <section className="relative z-10 mx-auto -mt-12 w-full max-w-[1240px] px-4 md:-mt-16 md:px-6">
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          {shortcuts.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="group flex flex-col gap-2 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-primary-300 md:gap-3 md:p-5"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-accent-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={`${item.icon} text-lg`} />
              </span>
              <div>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">{item.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-14 w-full max-w-[1240px] px-4 md:mt-20 md:px-6">
        <SectionHeading
          eyebrow="Featured Films"
          title="精选影片"
          description="从九十年代的开篇到最新篇章，按系列脉络挑选值得先看的几部作品。"
          actionText="查看全部片目"
          actionTo="/category"
        />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {featured.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="mx-auto mt-16 w-full max-w-[1240px] px-4 md:mt-24 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.55fr_1fr] lg:gap-12">
          <div>
            <SectionHeading
              eyebrow="Latest News"
              title="最新影视资讯"
              description="重映、修复、新作拍摄动态与影评观察，持续更新系列相关报道。"
              actionText="全部资讯"
              actionTo="/news"
            />
            <div className="rounded-lg border border-background-200/80 bg-background-50">
              {latestNews.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className={`group flex gap-4 p-4 transition-colors hover:bg-background-100 md:p-5 ${
                    index !== 0 ? "border-t border-background-200/80" : ""
                  }`}
                >
                  <div className="hidden h-[76px] w-[104px] shrink-0 overflow-hidden rounded-md bg-background-200 sm:block">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-400">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                        {item.category}
                      </span>
                      <span>{item.date}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-accent-600 md:text-base">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-1 text-xs text-foreground-500">{item.summary}</p>
                  </div>
                  <span className="hidden h-7 w-7 shrink-0 items-center justify-center self-center rounded-full border border-background-300 text-foreground-400 transition-colors group-hover:border-accent-400 group-hover:text-accent-600 md:flex">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading eyebrow="Topics" title="推荐专题合集" />
            <div className="flex flex-col gap-3">
              {topics.map((topic) => (
                <Link
                  key={topic.id}
                  to={`/news/${topic.id}`}
                  className="group relative block h-[150px] overflow-hidden rounded-lg"
                >
                  <img
                    src={topic.cover}
                    alt={topic.title}
                    title={`${topic.title} - 专题合集`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/10" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
                      专题
                    </span>
                    <h3 className="mt-2 line-clamp-2 font-heading text-sm font-semibold leading-snug text-background-50">
                      {topic.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-16 bg-background-100 py-14 md:mt-24 md:py-20">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
          <SectionHeading
            eyebrow="Series Timeline"
            title="系列年表"
            description="三十余年，八部片目。以时间为线索，快速理解系列的脉络与转折。"
            actionText="按时间浏览"
            actionTo="/category?sort=year-asc"
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-5">
            {timeline.map((movie) => (
              <Link key={movie.id} to={`/detail/${movie.id}`} className="group block">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-accent-500" />
                  <span className="h-px flex-1 bg-background-300" />
                </div>
                <p className="mt-3 font-heading text-2xl font-semibold text-primary-600">{movie.year}</p>
                <p className="mt-1 line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-accent-600">
                  {movie.title}
                </p>
                <p className="mt-1 text-[11px] text-foreground-500">{movie.genres.join(" · ")}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto mt-16 w-full max-w-[1240px] px-4 md:mt-24 md:px-6">
        <div className="grid overflow-hidden rounded-lg border border-background-200/80 bg-background-50 lg:grid-cols-2">
          <div className="h-[240px] w-full md:h-[320px] lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20quiet%20film%20archive%20reading%20room%20with%20wooden%20shelves%20of%20film%20books%2C%20vintage%20film%20canisters%20and%20a%20desk%20lamp%2C%20soft%20afternoon%20light%20through%20sheer%20curtains%2C%20cream%20and%20peach%20tones%2C%20editorial%20interior%20photography&width=1200&height=800&seq=home-about-archive&orientation=landscape"
              alt="蜜桃成熟时电影资料馆阅读空间"
              title="关于蜜桃成熟时电影资料站"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="flex flex-col justify-center gap-4 p-6 md:p-10">
            <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-accent-600">
              <span className="h-[2px] w-6 bg-accent-500" />
              About the Archive
            </p>
            <h2 className="font-heading text-xl font-semibold leading-snug text-foreground-950 md:text-2xl">
              把一段银幕记忆，整理成可以反复查阅的资料
            </h2>
            <p className="text-sm leading-relaxed text-foreground-600">
              我们按片目、人物与时间三条线索整理「蜜桃成熟时」系列，尽量把剧情梗概、制作信息与访谈资料放在同一个阅读路径里。这里没有夸张的标题，只有可核对的条目与安静的叙述。
            </p>
            <p className="text-sm leading-relaxed text-foreground-600">
              网站为纯前端演示项目，所有内容均为模拟数据，用于展示影视资料站的信息结构与浏览体验。
            </p>
            <Link
              to="/about"
              className="mt-1 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              了解网站定位
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}